import React from "react";
import { Box, Container, Typography, Paper, Grid, Stack, Chip, Button } from "@mui/material";
import { Link as RouterLink, useParams, Navigate } from "react-router-dom";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MarketingSiteHeader from "./MarketingSiteHeader";
import MarketingSiteFooter from "./MarketingSiteFooter";
import { getDevsPlaceholderContent } from "../data/devsPlaceholderContent";

/**
 * Developer / docs placeholder hub at /devs/:slug (production: www.quillai.app/devs/...).
 * Rough section layout; all copy is provisional until real docs ship.
 *
 * @returns {React.ReactElement}
 */
const DevsComingSoonPage = () => {
  const { slug } = useParams();
  const content = slug ? getDevsPlaceholderContent(slug) : null;

  if (!content) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <MarketingSiteHeader />

      <Box component="main" sx={{ flex: 1 }}>
        <Box
          sx={{
            py: { xs: 5, md: 8 },
            background: "linear-gradient(145deg, rgba(74,0,224,0.08) 0%, rgba(249,72,74,0.06) 45%, rgba(59,130,246,0.06) 100%)",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Container maxWidth="lg">
            <Button
              component={RouterLink}
              to="/"
              startIcon={<ArrowBackIcon />}
              color="inherit"
              sx={{ textTransform: "none", mb: 3, fontWeight: 600 }}
            >
              Home
            </Button>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1, mb: 2 }}>
              <Typography variant="overline" color="secondary" fontWeight={700} sx={{ letterSpacing: 1 }}>
                Developers
              </Typography>
              <Chip
                icon={<ConstructionOutlinedIcon sx={{ "&&": { fontSize: 18 } }} />}
                label="Coming soon"
                color="warning"
                size="small"
                sx={{ fontWeight: 700 }}
              />
            </Stack>
            <Typography variant="h3" component="h1" fontWeight={800} sx={{ mb: 2, maxWidth: 800 }}>
              {content.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.75 }}>
              {content.summary}
            </Typography>
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                border: "1px dashed",
                borderColor: "divider",
                bgcolor: "rgba(255,255,255,0.6)",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Production URLs will live at{" "}
                <Box component="span" fontWeight={700} color="text.primary">
                  https://www.quillai.app/devs/{slug}
                </Box>
                . This in-app preview uses the same paths for local testing.
              </Typography>
            </Paper>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
            Planned sections
          </Typography>
          <Grid container spacing={3}>
            {content.sections.map((row) => (
              <Grid item xs={12} md={4} key={row.heading}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Chip label="Coming soon" size="small" variant="outlined" sx={{ mb: 2, fontWeight: 600 }} />
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
                    {row.heading}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {row.blurb}
                  </Typography>
                  <Box
                    sx={{
                      mt: 2,
                      height: 4,
                      borderRadius: 1,
                      background: "linear-gradient(90deg, primary.main, secondary.main)",
                      opacity: 0.35,
                    }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <MarketingSiteFooter />
    </Box>
  );
};

export default DevsComingSoonPage;
